// User interaction tracking service for startup opportunities
import { 
  collection, 
  doc, 
  addDoc,
  updateDoc,
  increment,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  Timestamp
} from "firebase/firestore";
import { db } from "./firebase-config";
import { logEvent } from "../analytics";
import { ANALYTICS_EVENTS } from "./opportunity-analytics";

/**
 * Track user engagement with opportunity listings
 * @param {string} userId - The ID of the user
 * @param {Object} sessionData - Data about the user's session
 * @returns {Promise<Object>} - Success status or error
 */
export const trackUserEngagementSession = async (userId, sessionData) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const {
      sessionId = `session_${Date.now()}`,
      startTime,
      endTime = new Date(),
      opportunitiesViewed = [],
      detailsViewed = [],
      connectionsRequested = [],
      searchesPerformed = [],
      filtersUsed = [],
      source = 'web'
    } = sessionData;

    // Calculate session duration
    const sessionDuration = startTime ? 
      (endTime - new Date(startTime)) / 1000 : // in seconds
      null;

    // Create session record
    const sessionData = {
      userId,
      sessionId,
      startTime: startTime ? Timestamp.fromDate(new Date(startTime)) : serverTimestamp(),
      endTime: Timestamp.fromDate(new Date(endTime)),
      duration: sessionDuration,
      opportunityViewCount: opportunitiesViewed.length,
      detailsViewCount: detailsViewed.length,
      connectionRequestCount: connectionsRequested.length,
      searchCount: searchesPerformed.length,
      filterCount: filtersUsed.length,
      source,
      timestamp: serverTimestamp()
    };

    // Store session in user_engagement collection
    await addDoc(collection(db, 'user_engagement'), sessionData);

    // Track in Firebase Analytics
    await logEvent('user_engagement_session', {
      user_id: userId,
      session_id: sessionId,
      duration: sessionDuration,
      opportunity_view_count: opportunitiesViewed.length,
      details_view_count: detailsViewed.length,
      connection_request_count: connectionsRequested.length
    });

    return { success: true };
  } catch (error) {
    console.error('Error tracking user engagement session:', error);
    return {
      success: false,
      error: error.message || 'Failed to track user engagement session'
    };
  }
};

/**
 * Track opportunity conversion events
 * @param {string} opportunityId - The ID of the opportunity
 * @param {string} userId - The ID of the user
 * @param {string} conversionType - Type of conversion ('view_to_details', 'details_to_connection', 'connection_to_introduction')
 * @param {Object} context - Additional context about the conversion
 * @returns {Promise<Object>} - Success status or error
 */
export const trackOpportunityConversion = async (opportunityId, userId, conversionType, context = {}) => {
  try {
    if (!opportunityId || !userId || !conversionType) {
      return { success: false, error: 'Opportunity ID, User ID, and conversion type are required' };
    }

    // Create conversion event
    const conversionData = {
      opportunityId,
      userId,
      conversionType,
      timestamp: serverTimestamp(),
      context: {
        source: context.source || 'web',
        timeToConvert: context.timeToConvert || null, // Time from previous step to conversion
        ...context
      }
    };

    // Store in conversions collection
    await addDoc(collection(db, 'opportunity_conversions'), conversionData);

    // Update opportunity conversion metrics
    const opportunityRef = doc(db, 'startup_opportunities', opportunityId);
    const updateData = {};

    switch (conversionType) {
      case 'view_to_details':
        updateData.viewToDetailsCount = increment(1);
        break;
      case 'details_to_connection':
        updateData.detailsToConnectionCount = increment(1);
        break;
      case 'connection_to_introduction':
        updateData.connectionToIntroductionCount = increment(1);
        break;
    }

    if (Object.keys(updateData).length > 0) {
      await updateDoc(opportunityRef, updateData);
    }

    // Track in Firebase Analytics
    await logEvent('opportunity_conversion', {
      opportunity_id: opportunityId,
      user_id: userId,
      conversion_type: conversionType,
      source: context.source,
      time_to_convert: context.timeToConvert
    });

    return { success: true };
  } catch (error) {
    console.error('Error tracking opportunity conversion:', error);
    return {
      success: false,
      error: error.message || 'Failed to track opportunity conversion'
    };
  }
};

/**
 * Track user interaction with opportunity
 * @param {string} opportunityId - The ID of the opportunity
 * @param {string} userId - The ID of the user
 * @param {string} interactionType - Type of interaction ('click', 'hover', 'share', 'bookmark', etc.)
 * @param {Object} context - Additional context about the interaction
 * @returns {Promise<Object>} - Success status or error
 */
export const trackUserInteraction = async (opportunityId, userId, interactionType, context = {}) => {
  try {
    if (!opportunityId || !interactionType) {
      return { success: false, error: 'Opportunity ID and interaction type are required' };
    }

    // Create interaction event
    const interactionData = {
      opportunityId,
      userId: userId || null, // Allow anonymous tracking
      interactionType,
      timestamp: serverTimestamp(),
      context: {
        source: context.source || 'web',
        duration: context.duration || null, // Duration of interaction if applicable
        ...context
      }
    };

    // Store in interactions collection
    await addDoc(collection(db, 'opportunity_interactions'), interactionData);

    // Update opportunity interaction metrics
    const opportunityRef = doc(db, 'startup_opportunities', opportunityId);
    const updateData = {};

    switch (interactionType) {
      case 'click':
        updateData.clickCount = increment(1);
        break;
      case 'share':
        updateData.shareCount = increment(1);
        break;
      case 'bookmark':
        updateData.bookmarkCount = increment(1);
        break;
      case 'hover':
        updateData.hoverCount = increment(1);
        break;
    }

    if (Object.keys(updateData).length > 0) {
      await updateDoc(opportunityRef, updateData);
    }

    // Track in Firebase Analytics
    await logEvent('opportunity_interaction', {
      opportunity_id: opportunityId,
      user_id: userId,
      interaction_type: interactionType,
      source: context.source,
      duration: context.duration
    });

    return { success: true };
  } catch (error) {
    console.error('Error tracking user interaction:', error);
    return {
      success: false,
      error: error.message || 'Failed to track user interaction'
    };
  }
};

/**
 * Get user engagement metrics for a specific user
 * @param {string} userId - The ID of the user
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and engagement data or error
 */
export const getUserEngagementMetrics = async (userId, options = {}) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const {
      startDate = null,
      endDate = null,
      limit: limitCount = 100
    } = options;

    // Build query for user engagement
    let engagementQuery = collection(db, 'user_engagement');
    const queryConstraints = [where('userId', '==', userId)];

    // Add date filters if provided
    if (startDate) {
      queryConstraints.push(where('timestamp', '>=', startDate));
    }
    if (endDate) {
      queryConstraints.push(where('timestamp', '<=', endDate));
    }

    // Add ordering and limit
    queryConstraints.push(orderBy('timestamp', 'desc'));
    queryConstraints.push(limit(limitCount));

    engagementQuery = query(engagementQuery, ...queryConstraints);

    // Execute query
    const snapshot = await getDocs(engagementQuery);

    // Transform data
    const sessions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate summary metrics
    const summary = {
      totalSessions: sessions.length,
      totalSessionDuration: sessions.reduce((total, session) => total + (session.duration || 0), 0),
      averageSessionDuration: 0,
      totalOpportunityViews: sessions.reduce((total, session) => total + (session.opportunityViewCount || 0), 0),
      totalDetailsViews: sessions.reduce((total, session) => total + (session.detailsViewCount || 0), 0),
      totalConnectionRequests: sessions.reduce((total, session) => total + (session.connectionRequestCount || 0), 0),
      totalSearches: sessions.reduce((total, session) => total + (session.searchCount || 0), 0),
      totalFilters: sessions.reduce((total, session) => total + (session.filterCount || 0), 0)
    };

    // Calculate average session duration
    if (sessions.length > 0) {
      summary.averageSessionDuration = summary.totalSessionDuration / sessions.length;
    }

    // Get user's opportunity interactions
    const interactionsQuery = query(
      collection(db, 'opportunity_interactions'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    const interactionsSnapshot = await getDocs(interactionsQuery);
    const interactions = interactionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get user's opportunity conversions
    const conversionsQuery = query(
      collection(db, 'opportunity_conversions'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    const conversionsSnapshot = await getDocs(conversionsQuery);
    const conversions = conversionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      data: {
        sessions,
        interactions,
        conversions,
        summary
      }
    };
  } catch (error) {
    console.error('Error fetching user engagement metrics:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch user engagement metrics'
    };
  }
};

/**
 * Get opportunity performance metrics
 * @param {string} opportunityId - The ID of the opportunity
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and performance data or error
 */
export const getOpportunityPerformanceMetrics = async (opportunityId, options = {}) => {
  try {
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }

    const {
      startDate = null,
      endDate = null
    } = options;

    // Get opportunity document for base metrics
    const opportunityRef = doc(db, 'startup_opportunities', opportunityId);
    const opportunitySnap = await getDoc(opportunityRef);

    if (!opportunitySnap.exists()) {
      return { success: false, error: 'Opportunity not found' };
    }

    const opportunityData = opportunitySnap.data();

    // Build query for analytics events
    let analyticsQuery = collection(db, 'opportunity_analytics');
    const queryConstraints = [where('opportunityId', '==', opportunityId)];

    // Add date filters if provided
    if (startDate) {
      queryConstraints.push(where('timestamp', '>=', startDate));
    }
    if (endDate) {
      queryConstraints.push(where('timestamp', '<=', endDate));
    }

    // Add ordering
    queryConstraints.push(orderBy('timestamp', 'desc'));

    analyticsQuery = query(analyticsQuery, ...queryConstraints);

    // Execute query
    const analyticsSnapshot = await getDocs(analyticsQuery);
    const analyticsEvents = analyticsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get interactions
    let interactionsQuery = collection(db, 'opportunity_interactions');
    const interactionConstraints = [where('opportunityId', '==', opportunityId)];

    // Add date filters if provided
    if (startDate) {
      interactionConstraints.push(where('timestamp', '>=', startDate));
    }
    if (endDate) {
      interactionConstraints.push(where('timestamp', '<=', endDate));
    }

    // Add ordering
    interactionConstraints.push(orderBy('timestamp', 'desc'));

    interactionsQuery = query(interactionsQuery, ...interactionConstraints);

    // Execute query
    const interactionsSnapshot = await getDocs(interactionsQuery);
    const interactions = interactionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get conversions
    let conversionsQuery = collection(db, 'opportunity_conversions');
    const conversionConstraints = [where('opportunityId', '==', opportunityId)];

    // Add date filters if provided
    if (startDate) {
      conversionConstraints.push(where('timestamp', '>=', startDate));
    }
    if (endDate) {
      conversionConstraints.push(where('timestamp', '<=', endDate));
    }

    // Add ordering
    conversionConstraints.push(orderBy('timestamp', 'desc'));

    conversionsQuery = query(conversionsQuery, ...conversionConstraints);

    // Execute query
    const conversionsSnapshot = await getDocs(conversionsQuery);
    const conversions = conversionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate metrics
    const viewCount = opportunityData.viewCount || 0;
    const detailsViewCount = opportunityData.detailsViewCount || 0;
    const connectionRequestCount = opportunityData.connectionRequestCount || 0;
    const connectionApprovedCount = opportunityData.connectionApprovedCount || 0;
    const connectionIntroducedCount = opportunityData.connectionIntroducedCount || 0;

    const metrics = {
      viewCount,
      detailsViewCount,
      connectionRequestCount,
      connectionApprovedCount,
      connectionIntroducedCount,
      viewToDetailsRate: viewCount > 0 ? (detailsViewCount / viewCount * 100).toFixed(2) : 0,
      detailsToConnectionRate: detailsViewCount > 0 ? (connectionRequestCount / detailsViewCount * 100).toFixed(2) : 0,
      connectionToIntroductionRate: connectionRequestCount > 0 ? (connectionIntroducedCount / connectionRequestCount * 100).toFixed(2) : 0,
      overallConversionRate: viewCount > 0 ? (connectionIntroducedCount / viewCount * 100).toFixed(2) : 0,
      interactionCount: interactions.length,
      interactionsByType: interactions.reduce((acc, interaction) => {
        const type = interaction.interactionType;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {})
    };

    return {
      success: true,
      data: {
        metrics,
        analyticsEvents,
        interactions,
        conversions,
        opportunity: {
          id: opportunityId,
          companyName: opportunityData.companyName,
          fundingStage: opportunityData.fundingStage,
          roleArea: opportunityData.roleArea,
          status: opportunityData.status
        }
      }
    };
  } catch (error) {
    console.error('Error fetching opportunity performance metrics:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch opportunity performance metrics'
    };
  }
};

/**
 * Get conversion success metrics for all opportunities
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and conversion data or error
 */
export const getConversionSuccessMetrics = async (options = {}) => {
  try {
    const {
      startDate = null,
      endDate = null,
      limit: limitCount = 100
    } = options;

    // Get all opportunities with connection requests
    const opportunitiesQuery = query(
      collection(db, 'startup_opportunities'),
      where('connectionRequestCount', '>', 0),
      orderBy('connectionRequestCount', 'desc'),
      limit(limitCount)
    );

    const opportunitiesSnapshot = await getDocs(opportunitiesQuery);
    const opportunities = opportunitiesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate conversion metrics for each opportunity
    const opportunityMetrics = opportunities.map(opportunity => {
      const viewCount = opportunity.viewCount || 0;
      const detailsViewCount = opportunity.detailsViewCount || 0;
      const connectionRequestCount = opportunity.connectionRequestCount || 0;
      const connectionIntroducedCount = opportunity.connectionIntroducedCount || 0;

      return {
        id: opportunity.id,
        companyName: opportunity.companyName,
        fundingStage: opportunity.fundingStage,
        roleArea: opportunity.roleArea,
        status: opportunity.status,
        metrics: {
          viewCount,
          detailsViewCount,
          connectionRequestCount,
          connectionIntroducedCount,
          viewToDetailsRate: viewCount > 0 ? (detailsViewCount / viewCount * 100).toFixed(2) : 0,
          detailsToConnectionRate: detailsViewCount > 0 ? (connectionRequestCount / detailsViewCount * 100).toFixed(2) : 0,
          connectionToIntroductionRate: connectionRequestCount > 0 ? (connectionIntroducedCount / connectionRequestCount * 100).toFixed(2) : 0,
          overallConversionRate: viewCount > 0 ? (connectionIntroducedCount / viewCount * 100).toFixed(2) : 0
        }
      };
    });

    // Calculate overall metrics
    const totalViews = opportunities.reduce((total, opp) => total + (opp.viewCount || 0), 0);
    const totalDetailsViews = opportunities.reduce((total, opp) => total + (opp.detailsViewCount || 0), 0);
    const totalConnectionRequests = opportunities.reduce((total, opp) => total + (opp.connectionRequestCount || 0), 0);
    const totalIntroductions = opportunities.reduce((total, opp) => total + (opp.connectionIntroducedCount || 0), 0);

    const overallMetrics = {
      totalViews,
      totalDetailsViews,
      totalConnectionRequests,
      totalIntroductions,
      viewToDetailsRate: totalViews > 0 ? (totalDetailsViews / totalViews * 100).toFixed(2) : 0,
      detailsToConnectionRate: totalDetailsViews > 0 ? (totalConnectionRequests / totalDetailsViews * 100).toFixed(2) : 0,
      connectionToIntroductionRate: totalConnectionRequests > 0 ? (totalIntroductions / totalConnectionRequests * 100).toFixed(2) : 0,
      overallConversionRate: totalViews > 0 ? (totalIntroductions / totalViews * 100).toFixed(2) : 0
    };

    return {
      success: true,
      data: {
        opportunityMetrics,
        overallMetrics
      }
    };
  } catch (error) {
    console.error('Error fetching conversion success metrics:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch conversion success metrics'
    };
  }
};